import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Search, Sparkles, X, Loader2 } from "lucide-react";

export default function AIPowerSearch({ module, onResults, onClear, placeholder }) {
  const { api } = useAuth();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [interpretation, setInterpretation] = useState("");
  const [resultCount, setResultCount] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const executeSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setInterpretation("");
      setResultCount(null);
      onClear?.();
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/ai-assistant/power-search", {
        query: searchQuery.trim(),
        module,
        limit: 100,
      });
      setInterpretation(res.data?.interpretation || "");
      setResultCount(res.data?.total ?? 0);
      onResults?.(res.data?.results || [], res.data?.interpretation || "");
    } catch (err) {
      console.error("Power search failed:", err);
      setInterpretation("Search failed — showing all records");
      onClear?.();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      executeSearch(query);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setInterpretation("");
    setResultCount(null);
    onClear?.();
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-1.5" data-testid="ai-power-search">
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-600" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || `Try: "show me all critical items" or "find John in IT"...`}
            className="pl-10 pr-10 h-10 border-sky-200 focus:border-sky-400 bg-sky-50/30"
            data-testid="ai-power-search-input"
          />
          {query && !loading && (
            <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          )}
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-sky-600" />
          )}
        </div>
        <Button
          size="sm"
          onClick={() => executeSearch(query)}
          disabled={!query.trim() || loading}
          className="bg-sky-700 hover:bg-sky-800 h-10 px-4"
          data-testid="ai-power-search-btn"
        >
          <Search className="w-4 h-4 mr-1" />
          AI Search
        </Button>
      </div>
      {interpretation && (
        <div className="flex items-center gap-2 text-xs animate-in fade-in duration-200">
          <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-xs">
            <Sparkles className="w-3 h-3 mr-1" />
            {interpretation}
          </Badge>
          {resultCount !== null && (
            <span className="text-slate-500">{resultCount} result{resultCount !== 1 ? "s" : ""}</span>
          )}
          <button onClick={clearSearch} className="text-sky-600 hover:text-sky-800 font-medium ml-1">
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
